import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ErrorMessage } from '../ErrorMessage';

import './styles.scss';

interface Props {
    value?: string;
    length?: number;
    validChars?: string;
    placeholder?: string;
    autoFocus?: boolean;
    errors?: string[];
    onChange?: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

export const VerificationCodeInput = ({
    value,
    length = 6,
    validChars = 'A-Za-z0-9',
    placeholder,
    autoFocus = false,
    errors,
    onChange,
    onFocus,
    onBlur,
}: Props) => {
    const [localValue, setLocalValue] = useState('');
    const [isActive, setActive] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus();
        }
    }, [autoFocus]);

    const handleClick = () => {
        inputRef.current?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            // do not allow to change cursor position
            event.preventDefault();
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newInputVal = event.target.value.replace(/\s/g, '');

        if (RegExp(`^[${validChars}]{0,${length}}$`).test(newInputVal)) {
            if (onChange) {
                onChange?.(newInputVal);
            }
            setLocalValue(newInputVal);
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasteValue = e.clipboardData.getData('text').slice(0, length);

        if (RegExp(`^[${validChars}]{0,${length}}$`).test(pasteValue)) {
            if (onChange) {
                onChange?.(pasteValue);
            }
            setLocalValue(pasteValue);
        }
    };

    const getValue = () => {
        return value ?? localValue;
    };

    return (
        <div className='verification-code-input'>
            <input
                type='number'
                pattern='[0-9]*'
                inputMode='numeric'
                spellCheck={false}
                autoComplete='one-time-code'
                value={getValue()}
                onChange={handleInputChange}
                ref={inputRef}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={() => {
                    setActive(true);
                    onFocus?.();
                }}
                onBlur={() => {
                    setActive(false);
                    onBlur?.();
                }}
                className='verification-code-input__hidden-input'
            />
            <div className='verification-code-input__wrap' onClick={() => inputRef.current?.focus()}>
                {[...Array(length)].map((_, i) => (
                    <div
                        className={`verification-code-input__item ${
                            i === length / 2 - 1 ? 'verification-code-input__item--margin' : ''
                        } ${
                            (getValue().length === i || (getValue().length === i + 1 && length === i + 1)) && isActive
                                ? 'verification-code-input__item--selected'
                                : ''
                        } ${getValue().length < i ? 'verification-code-input__item--inactive' : ''} ${
                            errors?.length ? 'verification-code-input__item--has-error' : ''
                        }`}
                        onClick={handleClick}
                        id={`field-${i}`}
                        key={i}
                    >
                        {getValue()[i] || placeholder}
                    </div>
                ))}
            </div>

            <div className='verification-code-input__error'>
                <ErrorMessage errors={errors} align='center' />
            </div>
        </div>
    );
};
